/**
 * DryUI hooks for ChecksAndTicks.
 *
 * These hooks replace the React Query hooks (useAssets, useSites, useForms, etc.)
 * with DryStore-backed local-first queries.
 *
 * Pattern:
 * - useServerQuery reads from API and caches in local DryStore
 * - useDryMutation writes to server via DryConnect, then updates local store
 * - SyncManager handles background pull to keep local store fresh
 *
 * For pages that need server-side filtering/pagination (e.g., large asset lists),
 * we provide hybrid hooks that query the server but cache results locally.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCacheThroughQuery, useInfiniteQuery as useDryInfiniteQuery, useDoc } from '@drystuff/ui';
import { useDry, useCollection } from './DryContext';
import type { Collection } from '@drystuff/store';
import type {
  Asset, AssetCreateRequest, AssetType,
  Site, SiteCreateRequest, UserSite,
  FormTemplate, FormSubmission,
  User,
} from '../types';
import { api } from './contract';

// ============================================================================
// GENERIC HOOKS
// ============================================================================

/**
 * Extracts the items array from the backend response.
 * Backend now standardizes on { data: [...], total } for all list endpoints.
 */
function extractItems(responseData: any): any[] {
  const items = responseData?.data || (Array.isArray(responseData) ? responseData : []);
  return Array.isArray(items) ? items : [];
}

function extractTotal(responseData: any, items: any[]): number {
  return responseData?.total || items.length;
}

/**
 * Server-first query with local DryStore cache.
 * Uses DryUI's native useCacheThroughQuery — serves cached data immediately,
 * fetches from server in background, auto-updates when fresh data arrives.
 */
export function useServerQuery<T = any>(
  apiPath: string,
  params?: Record<string, any>,
  options?: { collection?: Collection<any>; cacheResults?: boolean }
) {
  const { http } = useDry();

  // Capture the real server `total` alongside the cached array. useCacheThroughQuery's
  // fetcher returns only the items array, so without this side-channel the cache branch
  // would fall back to the page length (e.g. "100" instead of the true total). Same
  // pattern as useInfiniteServerQuery. Declared before the branch so hook order is stable.
  const [serverTotal, setServerTotal] = useState(0);

  // If we have a collection, use native cache-through
  if (options?.collection) {
    const result = useCacheThroughQuery<T>(
      async () => {
        const response = await http.get<any>(apiPath, { params });
        const items = extractItems(response.data);
        setServerTotal(extractTotal(response.data, items));
        return items as T[];
      },
      options.collection,
      { staleTime: 30000 },
    );

    return {
      data: result.data as T | null,
      total: serverTotal || (Array.isArray(result.data) ? result.data.length : 0),
      loading: result.loading,
      isLoading: result.loading,
      isStale: (result as any).isStale || false,
      error: (result as any).error || null,
      refetch: (result as any).refresh || (() => {}),
    };
  }

  // No collection — plain server fetch (no caching). Refetch is wired up
  // via a refresh counter that's bumped imperatively; the useEffect reads
  // it and re-runs. Call sites that mutate the underlying entity (create,
  // update, delete) call refetch() after success to bring the list back
  // in sync.
  const [data, setData] = useState<T | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    http.get<any>(apiPath, { params })
      .then((response: any) => {
        if (cancelled) return;
        const items = extractItems(response.data);
        setData(items as any);
        setTotal(extractTotal(response.data, items));
        setLoading(false);
      })
      .catch((err: any) => {
        if (!cancelled) { setError(err); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [apiPath, paramsKey, refreshTick]);

  return {
    data,
    total,
    loading,
    isLoading: loading,
    error,
    refetch: () => setRefreshTick((t) => t + 1),
  };
}

/**
 * Mutation hook for non-CRUD server operations (publish, approve, archive, etc.)
 *
 * Re-throws errors from the mutationFn so call sites can `try { await
 * mutate(...) } catch (e) {}` in the standard async/await pattern.
 *
 * (Earlier this wrapped DryUI's `useAsyncMutation` whose `mutate`
 * swallows errors and returns null — that left every call site silently
 * succeeding on backend failures. The `Users.tsx` Add User flow's silent
 * failure on password-validation errors is the canonical example.)
 */
export function useDryMutation<TInput = any, TOutput = any>(
  mutationFn: (input: TInput) => Promise<TOutput>,
  _options?: {
    onSuccess?: (data: TOutput) => void;
    onError?: (error: Error) => void;
  }
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(async (input: TInput) => {
    setLoading(true);
    setError(null);
    try {
      const result = await mutationFn(input);
      setLoading(false);
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      setLoading(false);
      throw e;
    }
  }, [mutationFn]);

  return { mutate, loading, error };
}

// ============================================================================
// ASSET HOOKS
// ============================================================================

export function useAssets(params?: { limit?: number; status?: string; site_id?: string; search?: string; page?: number }) {
  const { db } = useDry();
  return useServerQuery<Asset[]>('/assets', params, {
    collection: db.assets,
    cacheResults: true,
  });
}

export function useAsset(id: string) {
  const { db } = useDry();
  // useDoc reads from local DryStore (pre-downloaded by SyncManager)
  // Works offline — no server fetch needed
  const { data, loading } = useDoc<Asset>(db.assets, id);
  return { data: data || null, loading, error: null };
}

export function useCreateAsset() {
  const { http, db } = useDry();
  return useDryMutation<AssetCreateRequest, Asset>(async (data) => {
    const response = await http.post<any>('/assets', data);
    const asset = response.data?.asset || response.data;
    try { await db.assets.upsert(asset); } catch { /* skip */ }
    return asset;
  });
}

export function useDeleteAsset() {
  const { http, db } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.delete<any>(`/assets/${id}`);
    try { await db.assets.delete(id); } catch { /* may not exist locally */ }
  });
}

export function useArchiveAsset() {
  const { http, db } = useDry();
  return useDryMutation<string, Asset>(async (id) => {
    const response = await http.post<any>(`/assets/${id}/archive`);
    const asset = response.data?.asset || response.data;
    if (asset) try { await db.assets.upsert(asset); } catch { /* skip */ }
    return asset;
  });
}

export function useUnarchiveAsset() {
  const { http, db } = useDry();
  return useDryMutation<string, Asset>(async (id) => {
    const response = await http.post<any>(`/assets/${id}/unarchive`);
    const asset = response.data?.asset || response.data;
    if (asset) try { await db.assets.upsert(asset); } catch { /* skip */ }
    return asset;
  });
}

export function useGenerateAssetQR() {
  const { http } = useDry();
  return useDryMutation<string, any>(async (id) => {
    const response = await http.post<any>(`/assets/${id}/qr`);
    return response.data;
  });
}

export function usePrefetchAsset() {
  // No-op in DryStore world. Data is already local from sync.
  return (_id: string) => {};
}

// ============================================================================
// ASSET TYPE HOOKS
// ============================================================================

export function useAssetTypes() {
  const { db, http } = useDry();
  // Cache-through: serve from local store, refresh from server
  // Backend now returns { data: [...] } consistently
  const result = useCacheThroughQuery<AssetType>(
    async () => {
      const r = await http.get<any>('/asset-types');
      return extractItems(r.data);
    },
    db.assetTypes,
    { staleTime: 60000 },
  );
  return { data: (result.data as AssetType[]) || [], isLoading: result.loading };
}

// ============================================================================
// SITE HOOKS
// ============================================================================

export function useSites(params?: { search?: string; site_type?: string; limit?: number }) {
  return useServerQuery<any>('/sites', params);
}

export function useSite(id: string) {
  const { db } = useDry();
  const { data, loading } = useDoc<Site>(db.sites, id);
  return { data: data || null, isLoading: loading };
}

export function useCreateSite() {
  const { http, db } = useDry();
  return useDryMutation<SiteCreateRequest, Site>(async (data) => {
    const response = await http.post<any>('/sites', data);
    const site = response.data;
    try { await db.sites.upsert(site); } catch { /* skip */ }
    return site;
  });
}

export function useUpdateSite() {
  const { http, db } = useDry();
  return useDryMutation<{ id: string; data: any }, Site>(async ({ id, data }) => {
    const response = await http.put<any>(`/sites/${id}`, data);
    const site = response.data;
    try { await db.sites.upsert(site); } catch { /* skip */ }
    return site;
  });
}

export function useDeleteSite() {
  const { http, db } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.delete<any>(`/sites/${id}`);
    try { await db.sites.delete(id); } catch { /* may not exist */ }
  });
}

export function useSiteUsers(siteId: string) {
  return useServerQuery<UserSite[]>(`/sites/${siteId}/users`);
}

export function useAssignUserToSite() {
  const { http } = useDry();
  return useDryMutation<{ siteId: string; data: any }, UserSite>(async ({ siteId, data }) => {
    const response = await http.post<any>(`/sites/${siteId}/users`, data);
    return response.data;
  });
}

export function useRemoveUserFromSite() {
  const { http } = useDry();
  return useDryMutation<{ siteId: string; userId: string }, void>(async ({ siteId, userId }) => {
    await http.delete<any>(`/sites/${siteId}/users/${userId}`);
  });
}

export function useSiteSummary(siteId: string) {
  return useServerObject<any>(`/sites/${siteId}/summary`);
}

// ============================================================================
// FORM HOOKS
// ============================================================================

export function useForms(params?: { status?: string; search?: string; limit?: number }) {
  const { db } = useDry();
  return useServerQuery<FormTemplate[]>('/forms', params, {
    collection: db.formTemplates,
    cacheResults: true,
  });
}

export function useForm(id: string) {
  const { db } = useDry();
  // useDoc reads from local DryStore (pre-downloaded by SyncManager)
  // Form templates are cached locally — works offline for QR scan flow
  const { data, loading } = useDoc<FormTemplate>(db.formTemplates, id);
  return { data: data || null, loading, error: null };
}

export function useDeleteForm() {
  const { http, db } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.delete<any>(`/forms/${id}`);
    try { await db.formTemplates.delete(id); } catch { /* skip */ }
  });
}

export function usePublishForm() {
  const { http, db } = useDry();
  return useDryMutation<string, FormTemplate>(async (id) => {
    const response = await http.post<any>(`/forms/${id}/publish`);
    const form = response.data?.form || response.data;
    if (form) try { await db.formTemplates.upsert(form); } catch { /* skip */ }
    return form;
  });
}

export function useArchiveForm() {
  const { http, db } = useDry();
  return useDryMutation<string, FormTemplate>(async (id) => {
    const response = await http.post<any>(`/forms/${id}/archive`);
    const form = response.data?.form || response.data;
    if (form) try { await db.formTemplates.upsert(form); } catch { /* skip */ }
    return form;
  });
}

export function useUnarchiveForm() {
  const { http, db } = useDry();
  return useDryMutation<string, FormTemplate>(async (id) => {
    const response = await http.post<any>(`/forms/${id}/unarchive`);
    const form = response.data?.form || response.data;
    if (form) try { await db.formTemplates.upsert(form); } catch { /* skip */ }
    return form;
  });
}

export function useDuplicateForm() {
  const { http, db } = useDry();
  return useDryMutation<string, FormTemplate>(async (id) => {
    const response = await http.post<any>(`/forms/${id}/duplicate`);
    const form = response.data?.form || response.data;
    if (form) try { await db.formTemplates.upsert(form); } catch { /* skip */ }
    return form;
  });
}

export function usePrefetchForm() {
  // No-op in DryStore world
  return (_id: string) => {};
}

// ============================================================================
// SUBMISSION HOOKS
// ============================================================================

export function useSubmissions(params?: { status?: string; form_template_id?: string; limit?: number }) {
  return useServerQuery<FormSubmission[]>('/submissions', params);
}

export function useSubmission(id: string) {
  const { http } = useDry();
  const [data, setData] = useState<FormSubmission | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    http.get<any>(`/submissions/${id}`)
      .then((r: any) => !cancelled && setData(r.data?.submission || r.data))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id, http]);

  return { data, isLoading: loading };
}

export function useApproveSubmission() {
  const { http } = useDry();
  return useDryMutation<{ id: string; notes?: string }, FormSubmission>(async ({ id, notes }) => {
    const response = await http.post<any>(`/submissions/${id}/approve`, { notes });
    return response.data?.submission || response.data;
  });
}

export function useRejectSubmission() {
  const { http } = useDry();
  return useDryMutation<{ id: string; notes?: string }, FormSubmission>(async ({ id, notes }) => {
    const response = await http.post<any>(`/submissions/${id}/reject`, { notes });
    return response.data?.submission || response.data;
  });
}

export function useCreateSubmission() {
  const { http, db } = useDry();
  return useDryMutation<any, FormSubmission>(async (data) => {
    // Mint an idempotency_key when the caller didn't supply one, so a retried
    // POST (network blip, double-tap) can't create a duplicate submission row.
    const body = data?.idempotency_key
      ? data
      : { ...data, idempotency_key: crypto.randomUUID() };
    const response = await http.post<any>('/submissions', body);
    const submission = response.data?.submission || response.data;
    try { await db.formSubmissions.upsert(submission); } catch { /* skip */ }
    return submission;
  });
}

// ============================================================================
// USER HOOKS
// ============================================================================

export function useUsers(params?: { role?: string; search?: string }) {
  return useServerQuery<User[]>('/users', params);
}

export function useCreateUser() {
  const { http } = useDry();
  return useDryMutation<any, User>(async (data) => {
    const response = await http.post<any>('/users', data);
    return response.data?.user || response.data;
  });
}

export function useUpdateUser() {
  const { http } = useDry();
  return useDryMutation<{ id: string; data: any }, User>(async ({ id, data }) => {
    const response = await http.put<any>(`/users/${id}`, data);
    return response.data?.user || response.data;
  });
}

export function useDeleteUser() {
  const { http } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.delete<any>(`/users/${id}`);
  });
}

// ============================================================================
// DASHBOARD HOOKS
// ============================================================================

/**
 * Fetches a single-object endpoint (not a list).
 * useServerQuery runs every response through extractItems which expects an
 * array — a flat object becomes [], swallowing the data. This helper is for
 * endpoints that return one object (analytics, summaries, etc.).
 */
function useServerObject<T = any>(apiPath: string) {
  const { http } = useDry();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    http.get<any>(apiPath)
      .then((response: any) => {
        if (cancelled) return;
        const body = response?.data ?? response;
        setData((body?.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : body) as T);
        setLoading(false);
      })
      .catch((err: any) => {
        if (!cancelled) { setError(err); setLoading(false); }
      });
    return () => { cancelled = true; };
  }, [apiPath]);

  return { data, isLoading: loading, error };
}

export function useDashboardStats() {
  return useServerObject<any>('/analytics/dashboard');
}

// ============================================================================
// INFINITE SCROLL HOOK (native DryUI useInfiniteQuery)
// ============================================================================

/**
 * Generic infinite scroll hook using DryUI's native useInfiniteQuery.
 * Wraps an API path with pagination into the expected fetcher signature.
 */
export function useInfiniteServerQuery<T = any>(
  apiPath: string,
  params?: Record<string, any>,
  options?: { pageSize?: number; collection?: Collection<any> }
) {
  const { http } = useDry();
  const pageSize = options?.pageSize || 20;
  const paramsKey = JSON.stringify(params);

  // DryUI's useInfiniteQuery consumes `total` internally for hasMore but does
  // NOT expose it on its return value, so we capture the real server total
  // ourselves from the fetcher. Without this the page falls back to the first
  // page length (e.g. shows "20 of 427").
  const [serverTotal, setServerTotal] = useState(0);

  const result = useDryInfiniteQuery<T>(
    async (page: number) => {
      const response = await http.get<any>(apiPath, {
        params: { ...params, page, limit: pageSize },
      });
      const items = extractItems(response.data);
      const total = extractTotal(response.data, items);
      setServerTotal(total);
      return { items: items as T[], total };
    },
    { pageSize },
  );

  // Reset when params change (e.g. filter/search)
  const prevParamsKey = useRef(paramsKey);
  useEffect(() => {
    if (prevParamsKey.current !== paramsKey) {
      prevParamsKey.current = paramsKey;
      (result as any).refresh?.();
    }
  }, [paramsKey]);

  // Map DryUI's return shape to what our pages expect
  return {
    data: result.data || [],
    total: serverTotal || result.data?.length || 0,
    loading: result.loading,
    error: (result as any).error || null,
    hasNextPage: result.hasMore,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.loading && (result.data?.length || 0) > 0,
  };
}

/**
 * Infinite scroll for Assets page.
 */
export function useInfiniteAssets(params?: { limit?: number; status?: string; site_id?: string; search?: string }) {
  return useInfiniteServerQuery<Asset>('/assets', params, {
    pageSize: params?.limit || 20,
  });
}

// ============================================================================
// MAINTENANCE HOOKS
// ============================================================================

export function useMaintenanceSchedules(params?: any) {
  return useServerQuery<any[]>('/maintenance/schedules', params);
}

export function useMaintenanceSchedule(id: string) {
  const { http } = useDry();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!id) return;
    let c = false;
    http.get<any>(`/maintenance/schedules/${id}`).then((r: any) => !c && setData(r.data)).catch(() => {}).finally(() => !c && setLoading(false));
    return () => { c = true; };
  }, [id, http]);
  return { data, isLoading: loading };
}

export function useCreateMaintenanceSchedule() {
  const { http } = useDry();
  return useDryMutation<any, any>(async (data) => {
    const response = await http.post<any>('/maintenance/schedules', data);
    return response.data;
  });
}

export function useUpdateMaintenanceSchedule() {
  const { http } = useDry();
  return useDryMutation<{ id: string; data: any }, any>(async ({ id, data }) => {
    const response = await http.put<any>(`/maintenance/schedules/${id}`, data);
    return response.data;
  });
}

export function useDeleteMaintenanceSchedule() {
  const { http } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.delete<any>(`/maintenance/schedules/${id}`);
  });
}

export function useMaintenanceTasks(params?: any) {
  return useServerQuery<any[]>('/maintenance/tasks', params);
}

export function useUpcomingMaintenanceTasks(days?: number) {
  return useServerQuery<any[]>('/maintenance/tasks/upcoming', { days: days || 7 });
}

export function useOverdueMaintenanceTasks() {
  return useServerQuery<any[]>('/maintenance/tasks/overdue');
}

// ============================================================================
// FORM ASSIGNMENT HOOKS
// ============================================================================

export function useFormAssignments(params?: any) {
  return useServerQuery<any[]>('/form-assignments', params);
}

export function useCreateFormAssignment() {
  const { http } = useDry();
  return useDryMutation<any, any>(async (data) => {
    const response = await http.post<any>('/form-assignments', data);
    return response.data;
  });
}

export function useCancelFormAssignment() {
  const { http } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.delete<any>(`/form-assignments/${id}`);
  });
}

export function useMyPendingForms() {
  return useServerQuery<any[]>('/form-assignments/my-pending');
}

// ============================================================================
// TEMPLATE LIBRARY HOOKS
// ============================================================================

export function useTemplateLibrary(params?: any) {
  return useServerQuery<any[]>('/templates', params);
}

export function useCloneTemplate() {
  const { http } = useDry();
  return useDryMutation<{ id: string; data?: any }, any>(async ({ id, data }) => {
    const response = await http.post<any>(`/templates/${id}/clone`, data || {});
    return response.data;
  });
}

// ============================================================================
// ISSUE HOOKS
// ============================================================================

export function useIssues(params?: any) {
  return useServerQuery<any[]>('/issues', params);
}

// Issue status breakdown for the tenant (open/acknowledged/in_progress/...).
// Backend wraps the payload in { success, data }; useServerObject unwraps `data`.
export function useIssueStats() {
  return useServerObject<any>('/issues/stats');
}

export function useIssue(id: string) {
  const { http } = useDry();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!id) return;
    let c = false;
    http.get<any>(`/issues/${id}`).then((r: any) => !c && setData(r.data?.issue || r.data)).catch(() => {}).finally(() => !c && setLoading(false));
    return () => { c = true; };
  }, [id, http]);
  return { data, isLoading: loading };
}

export function useUpdateIssue() {
  const { http } = useDry();
  return useDryMutation<{ id: string; data: any }, any>(async ({ id, data }) => {
    const response = await http.put<any>(`/issues/${id}`, data);
    return response.data;
  });
}

// ============================================================================
// COMMENT HOOKS (Submission comments)
// ============================================================================

export function useComments(submissionId: string) {
  return useServerQuery<any[]>(`/submissions/${submissionId}/comments`);
}

export function useCreateComment() {
  const { http } = useDry();
  return useDryMutation<{ submissionId: string; data: any }, any>(async ({ submissionId, data }) => {
    const response = await http.post<any>(`/submissions/${submissionId}/comments`, data);
    return response.data;
  });
}

export function useUpdateComment() {
  const { http } = useDry();
  return useDryMutation<{ submissionId: string; commentId: string; data: any }, any>(async ({ submissionId, commentId, data }) => {
    const response = await http.put<any>(`/submissions/${submissionId}/comments/${commentId}`, data);
    return response.data;
  });
}

export function useDeleteComment() {
  const { http } = useDry();
  return useDryMutation<{ submissionId: string; commentId: string }, void>(async ({ submissionId, commentId }) => {
    await http.delete<any>(`/submissions/${submissionId}/comments/${commentId}`);
  });
}

// ============================================================================
// TENANT / SETTINGS HOOKS
// ============================================================================

export function useTenant() {
  return useServerQuery<any>('/tenants/current');
}

export function useUpdateTenant() {
  const { http } = useDry();
  return useDryMutation<any, any>(async (data) => {
    const response = await http.put<any>('/tenants/current', data);
    return response.data;
  });
}

// ============================================================================
// PROFILE / PREFERENCES HOOKS
// ============================================================================

export function useUserPreferences() {
  return useServerQuery<any>('/users/me/preferences');
}

export function useUpdateUserPreferences() {
  const { http } = useDry();
  return useDryMutation<any, any>(async (data) => {
    const response = await http.put<any>('/users/me/preferences', data);
    return response.data;
  });
}

export function useChangePassword() {
  const { http } = useDry();
  return useDryMutation<{ current_password: string; new_password: string }, any>(async (data) => {
    const response = await http.post<any>('/auth/change-password', data);
    return response.data;
  });
}

// ============================================================================
// BILLING HOOKS
// ============================================================================

export function useSubscription() {
  return useServerQuery<any>('/billing/subscription');
}

export function useInvoices() {
  return useServerQuery<any[]>('/billing/invoices');
}

export function useCreateSubscription() {
  const { http } = useDry();
  return useDryMutation<any, any>(async (data) => {
    const response = await http.post<any>('/billing/subscription', data);
    return response.data;
  });
}

export function useCancelSubscription() {
  const { http } = useDry();
  return useDryMutation<void, any>(async () => {
    const response = await http.delete<any>('/billing/subscription');
    return response.data;
  });
}

// ============================================================================
// ANALYTICS HOOKS
// ============================================================================

export function useAnalyticsDashboard(params?: any) {
  return useServerQuery<any>('/analytics/dashboard', params);
}

export function useAnalyticsTrends(params?: any) {
  return useServerQuery<any>('/analytics/trends', params);
}

export function useAnalyticsPerformance(params?: any) {
  return useServerQuery<any>('/analytics/performance', params);
}

// ============================================================================
// ASSET-FORM LINK HOOKS
// ============================================================================

export function useAssetFormLinks(params?: any) {
  return useServerQuery<any[]>('/asset-form-links', params);
}

export function useCreateAssetFormLink() {
  const { http } = useDry();
  return useDryMutation<any, any>(async (data) => {
    const response = await http.post<any>('/asset-form-links', data);
    return response.data;
  });
}

export function useDeleteAssetFormLink() {
  const { http } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.delete<any>(`/asset-form-links/${id}`);
  });
}

export function useBulkCreateAssetFormLinks() {
  const { http } = useDry();
  return useDryMutation<any, any>(async (data) => {
    const response = await http.post<any>('/asset-form-links/bulk', data);
    return response.data;
  });
}

// ============================================================================
// QR/NFC HOOKS
// ============================================================================

export function useGenerateQRCode() {
  const { http } = useDry();
  return useDryMutation<{ assetId: string; size?: number }, any>(async ({ assetId, size }) => {
    const response = await http.post<any>(`/qr-nfc/assets/${assetId}/qr`, { size: size || 256 });
    return response.data;
  });
}

// ============================================================================
// NOTIFICATION HOOKS
// ============================================================================

export function useNotifications() {
  return useServerQuery<any[]>('/notifications');
}

export function useMarkNotificationRead() {
  const { http } = useDry();
  return useDryMutation<string, void>(async (id) => {
    await http.put<any>(`/notifications/${id}/read`);
  });
}

export function useMarkAllNotificationsRead() {
  const { http } = useDry();
  return useDryMutation<void, void>(async () => {
    await http.put<any>('/notifications/read-all');
  });
}

// ============================================================================
// ADMIN HOOKS
// ============================================================================

export function useAdminErrors(params?: any) {
  return useServerQuery<any[]>('/admin/errors', params);
}

export function useAdminAlerts(params?: any) {
  return useServerQuery<any[]>('/admin/alerts', params);
}

export function useAdminMetrics(params?: any) {
  return useServerQuery<any>('/admin/metrics', params);
}

// ============================================================================
// UPLOAD HOOKS (keep presigned URL flow)
// ============================================================================

export function usePresignedUpload() {
  const { http } = useDry();
  return useDryMutation<{ filename: string; contentType: string }, any>(async (data) => {
    const response = await http.post<any>('/uploads/presigned-upload-url', data);
    return response.data;
  });
}

export function usePresignedDownload() {
  const { http } = useDry();
  return useDryMutation<string, any>(async (key) => {
    const response = await http.get<any>('/uploads/presigned-download-url', { params: { key } });
    return response.data;
  });
}

// ============================================================================
// RAW HTTP ACCESS (for pages that need direct API calls)
// ============================================================================

/**
 * Hook to get the DryConnect HTTP client directly.
 * Use this for pages that make complex or one-off API calls
 * that don't fit the useServerQuery/useDryMutation pattern.
 */
export function useHttp() {
  const { http } = useDry();
  return http;
}
